import { useState } from "react";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "./ui/dialog";
import { Instagram, Music2, ImagePlus, FileText, Plus, Trash2 } from "lucide-react";
import EventPicker from "./EventPicker";
import api from "../lib/api";
import { toast } from "sonner";

const PLATFORMS = [
  { key: "instagram", label: "Instagram", icon: Instagram },
  { key: "tiktok", label: "TikTok", icon: Music2 },
  { key: "story", label: "Story", icon: ImagePlus },
  { key: "flyer", label: "Flyer Drop", icon: FileText },
];

const STATUS_BADGE = {
  draft: "bg-slate-800 text-slate-300 border-slate-600",
  scheduled: "bg-amber-950 text-amber-300 border-amber-700",
  published: "bg-emerald-950 text-emerald-300 border-emerald-700",
};

export default function SocialPlannerView({ posts, events, onRefresh }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    platform: "instagram", caption: "", scheduled_for: "",
    image_url: "", event_id: "", status: "draft",
  });
  const [tab, setTab] = useState("all");

  const filtered = tab === "all" ? posts : posts.filter(p => p.status === tab);

  const create = async () => {
    if (!form.caption || !form.scheduled_for) { toast.error("Caption & date required"); return; }
    try {
      await api.post("/posts", form);
      toast.success("Post added to planner");
      setOpen(false);
      setForm({ platform: "instagram", caption: "", scheduled_for: "", image_url: "", event_id: "", status: "draft" });
      onRefresh();
    } catch { toast.error("Failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this post?")) return;
    await api.delete(`/posts/${id}`);
    onRefresh();
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl text-[var(--sn-gold)]">Social Media Planner</h2>
          <p className="text-slate-500 text-sm">Draft, plan, schedule chapter content.</p>
        </div>
        <Button data-testid="create-post-button" onClick={() => setOpen(true)}
          className="bg-[var(--sn-gold)] hover:brightness-95 text-black font-bold gap-2">
          <Plus className="w-4 h-4" /> New Post
        </Button>
      </div>

      <div className="flex gap-2">
        {["all","draft","scheduled","published"].map(k => (
          <button key={k} data-testid={`planner-tab-${k}`} onClick={() => setTab(k)}
            className={`text-xs px-3 py-1.5 rounded-full border capitalize ${
              tab === k ? "bg-[var(--sn-gold)] text-black border-[var(--sn-gold)]"
                        : "bg-slate-900 text-slate-400 border-slate-700 hover:border-[var(--sn-gold)]/60"
            }`}>{k}</button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <Card className="glass-card p-10 text-center text-slate-500">No posts yet — click &ldquo;New Post&rdquo; to draft one.</Card>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filtered.map(p => {
            const plat = PLATFORMS.find(x => x.key === p.platform);
            const Icon = plat?.icon || Instagram;
            const ev = events.find(e => e.id === p.event_id);
            return (
              <Card key={p.id} className="glass-card p-4 space-y-3" data-testid={`post-card-${p.id}`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2 text-slate-300 text-sm">
                    <Icon className="w-4 h-4 text-amber-400" /> {plat?.label}
                  </div>
                  <Badge className={STATUS_BADGE[p.status]}>{p.status}</Badge>
                </div>
                {p.image_url && (
                  <div className="aspect-square rounded-lg overflow-hidden bg-slate-900">
                    <img src={p.image_url} alt="" className="w-full h-full object-cover" />
                  </div>
                )}
                <p className="text-sm text-slate-300 line-clamp-4 whitespace-pre-line">{p.caption}</p>
                {ev && <p className="text-[11px] text-amber-400">Linked → {ev.title} · {ev.date}</p>}
                <div className="flex items-center justify-between text-xs text-slate-500 pt-2 border-t border-white/5">
                  <span className="font-mono">{new Date(p.scheduled_for).toLocaleString()}</span>
                  <Button variant="ghost" size="icon" onClick={() => remove(p.id)}
                          data-testid={`delete-post-${p.id}`}
                          className="text-slate-500 hover:text-rose-400 h-7 w-7">
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-slate-950 border-slate-800 max-w-lg" data-testid="create-post-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl text-amber-300">Draft Post</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1.5">
              <Label>Platform</Label>
              <Select value={form.platform} onValueChange={(v) => setForm({ ...form, platform: v })}>
                <SelectTrigger data-testid="post-platform"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-slate-900 border-slate-700">
                  {PLATFORMS.map(p => <SelectItem key={p.key} value={p.key}>{p.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1.5">
              <Label>Caption</Label>
              <Textarea data-testid="post-caption" rows={4} value={form.caption}
                onChange={(e) => setForm({ ...form, caption: e.target.value })}
                placeholder="Write your caption… #Founders" />
              <div className="text-xs text-slate-500 text-right">{form.caption.length} chars</div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5">
                <Label>Scheduled for</Label>
                <Input data-testid="post-date" type="datetime-local" value={form.scheduled_for}
                  onChange={(e) => setForm({ ...form, scheduled_for: e.target.value })} />
              </div>
              <div className="space-y-1.5">
                <Label>Status</Label>
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="post-status"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-slate-900 border-slate-700">
                    <SelectItem value="draft">Draft</SelectItem>
                    <SelectItem value="scheduled">Scheduled</SelectItem>
                    <SelectItem value="published">Published</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-1.5">
              <Label>Image URL (optional)</Label>
              <Input data-testid="post-image" value={form.image_url}
                onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                placeholder="https://…" />
            </div>
            <div className="space-y-1.5">
              <Label>Link to Event (optional)</Label>
              <EventPicker events={events.filter(e => e.status === "approved")} value={form.event_id}
                onChange={(v) => setForm({ ...form, event_id: v })}
                testid="post-event-link" placeholder="Search events to link…" allowNone />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)} className="border-slate-700 text-slate-300">Cancel</Button>
            <Button data-testid="submit-post-button" onClick={create} className="bg-[var(--sn-gold)] text-black hover:brightness-95">
              Save Post
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
